import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useLocations } from "../hooks/use-locations";
import { useUserLocation } from "../hooks/use-user-location";
import { useGeolocation, calculateDistance } from "../hooks/use-geolocation";
import { formatLocationHours, getStoreStatus } from "../lib/format-hours";
import { isCurrentlyActive } from "@/lib/date-utils";
import { Location, LocationAd, LocationService, RichTextBlock } from "../types/locations";
import { Badge } from "@/components/ui/badge";
import { AdViewerSheet } from "@/Ads/components/AdViewerSheet";
import { RichTextRenderer } from "@/components/RichTextRenderer";
import { batchGeocodeAddresses } from "@/lib/geocode-cache";
import { BASEMAP_ATTRIBUTION, BASEMAP_MAX_ZOOM, BASEMAP_TILE_URL } from "@/lib/basemap";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import { renderToStaticMarkup } from 'react-dom/server';
import { IconArrowRight, IconBrandFacebook, IconBrandInstagram, IconBrandLinkedin, IconBrandPinterest, IconBrandTiktok, IconBrandX, IconBrandYoutube, IconCheck, IconClock, IconCurrentLocation, IconExternalLink, IconFileText, IconInfoCircle, IconMail, IconMapPin, IconMapPinFilled, IconPhone, IconShoppingCart, IconTag, type TablerIcon } from '@tabler/icons-react';

// Helper to extract text from rich text children
const extractHeadingText = (block: RichTextBlock): string => {
  return block.children
    .map((child) => {
      if ('text' in child) {
        return (child as { text: string }).text;
      }
      return '';
    })
    .join('');
};

// Parse rich text content into sections split by headings
interface ContentSection {
  title: string;
  content: RichTextBlock[];
}

const parseContentSections = (content: RichTextBlock[] | string | undefined): ContentSection[] => {
  if (!content || typeof content === 'string' || !Array.isArray(content)) {
    return [];
  }

  const sections: ContentSection[] = [];
  let currentSection: ContentSection | null = null;

  for (const block of content) {
    if (block.type === 'heading') {
      // Start a new section
      if (currentSection) {
        sections.push(currentSection);
      }
      currentSection = {
        title: extractHeadingText(block),
        content: [], // Don't include the heading itself in content - it's the tab title
      };
    } else if (currentSection) {
      // Add content to current section
      currentSection.content.push(block);
    } else {
      // Content before any heading - create an "Overview" section
      if (sections.length === 0 && !currentSection) {
        currentSection = {
          title: 'Overview',
          content: [block],
        };
      }
    }
  }

  // Push the last section
  if (currentSection) {
    sections.push(currentSection);
  }

  return sections;
};

interface LocationWithDistance extends Location {
  distance?: number;
  coordinates?: [number, number];
}

const DEFAULT_CENTER: [number, number] = [47.6062, -122.3321];

// Render the Tabler pin to static markup for the Leaflet divIcon
const getMarkerSvg = (isHighlighted = false) => {
  const size = isHighlighted ? 40 : 32;
  return renderToStaticMarkup(
    <IconMapPinFilled
      size={size}
      style={{ color: 'hsl(var(--primary))', fill: 'currentColor' }}
    />,
  );
};

const getLinkInfo = (url: string): { icon: TablerIcon; label: string } => {
  const lowerUrl = url.toLowerCase();

  if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.com')) {
    return { icon: IconBrandFacebook, label: 'Facebook' };
  }
  if (lowerUrl.includes('instagram.com')) {
    return { icon: IconBrandInstagram, label: 'Instagram' };
  }
  if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) {
    return { icon: IconBrandX, label: 'X / Twitter' };
  }
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
    return { icon: IconBrandYoutube, label: 'YouTube' };
  }
  if (lowerUrl.includes('linkedin.com')) {
    return { icon: IconBrandLinkedin, label: 'LinkedIn' };
  }
  if (lowerUrl.includes('tiktok.com')) {
    return { icon: IconBrandTiktok, label: 'TikTok' };
  }
  if (lowerUrl.includes('pinterest.com')) {
    return { icon: IconBrandPinterest, label: 'Pinterest' };
  }
  if (lowerUrl.includes('mailto:')) {
    return { icon: IconMail, label: 'Email' };
  }
  if (lowerUrl.includes('shop') || lowerUrl.includes('instacart.com') || lowerUrl.includes('egrowcery.com')|| lowerUrl.includes('doordash.com') || lowerUrl.includes('store') || lowerUrl.includes('cart') || lowerUrl.includes('order')) {
    return { icon: IconShoppingCart, label: 'Shop Online' };
  }

  return { icon: IconExternalLink, label: 'Visit Site' };
};

const createCustomIcon = (isHighlighted = false) => {
  const size = isHighlighted ? 40 : 32;
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="marker-container" style="${isHighlighted ? 'filter: drop-shadow(0 0 8px hsl(var(--primary)));' : ''}">
        ${getMarkerSvg(isHighlighted)}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
};

const LocationDetailsDialog = ({ location, open, onOpenChange }: { location: Location | null; open: boolean; onOpenChange: (open: boolean) => void }) => {
  const [adViewerOpen, setAdViewerOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState<LocationAd | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_CENTER);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [serviceSheetOpen, setServiceSheetOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<LocationService | null>(null);

  useEffect(() => {
    if (!location || !open) return;

    const addressString = [
      location.LocationAddress.LocationAddressLineOne,
      location.LocationAddress.LocationAddressCity,
      location.LocationAddress.LocationAddressState,
      location.LocationAddress.LocationAddressZipCode,
    ].filter(Boolean).join(', ');

    if (!addressString) return;

    setIsGeocoding(true);
    fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressString)}&limit=1`, {
      headers: { 'User-Agent': 'StoreLocator/1.0' }
    })
      .then(res => res.json())
      .then(data => {
        if (data?.length > 0) {
          const coords: [number, number] = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
          setMapCenter(coords);
        }
      })
      .catch(error => {
        console.error('Geocoding error:', error);
      })
      .finally(() => {
        setIsGeocoding(false);
      });
  }, [location, open]);

  if (!location) return null;

  const formattedHours = formatLocationHours(location.LocationHours);
  const fullAddress = [
    location.LocationAddress.LocationAddressLineOne,
    location.LocationAddress.LocationAddressLineTwo,
    `${location.LocationAddress.LocationAddressCity}, ${location.LocationAddress.LocationAddressState} ${location.LocationAddress.LocationAddressZipCode}`,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-2xl">{location.LocationTitle}</DialogTitle>
          {location.LocationBanner && (
            <DialogDescription className="text-base !mt-0">{location.LocationBanner}</DialogDescription>
          )}
        </DialogHeader>

        {/* Map */}
        <Card className="overflow-hidden rounded-lg border shadow-sm">
          {isGeocoding ? (
            <Skeleton className="h-[200px] w-full" />
          ) : (
            <div className="h-[200px] w-full relative">
              <MapContainer
                key={`${mapCenter[0]}-${mapCenter[1]}`}
                center={mapCenter}
                zoom={15}
                style={{ height: '100%', width: '100%' }}
                zoomControl={true}
                className="rounded-lg"
              >
                <TileLayer
                  attribution={BASEMAP_ATTRIBUTION}
                  url={BASEMAP_TILE_URL}
                  maxZoom={BASEMAP_MAX_ZOOM}
                />
                <Marker position={mapCenter} icon={createCustomIcon()}>
                  <Popup className="custom-popup" closeButton={true}>
                    <div className="text-sm space-y-1 min-w-[180px]">
                      <p className="font-semibold text-foreground">{location.LocationTitle}</p>
                      <p className="text-muted-foreground text-xs">{location.LocationAddress.LocationAddressLineOne}</p>
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          )}
        </Card>

        <div className="space-y-6 mt-4">
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <IconMapPin className="h-5 w-5 mt-0.5 flex-shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium">Address</p>
                <p className="text-sm text-muted-foreground">{fullAddress}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <IconPhone className="h-5 w-5 flex-shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium">Phone</p>
                <p className="text-sm text-muted-foreground">{location.LocationPhoneNumber}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <IconClock className="h-5 w-5 mt-0.5 flex-shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium">Hours</p>
                <p className="text-sm text-muted-foreground whitespace-pre-line">{formattedHours}</p>
              </div>
            </div>
          </div>

          {location.LocationCoupons && (
            <div>
              <Badge className="mb-2 bg-emerald-500 hover:bg-emerald-600 text-white">
                <IconTag className="h-3 w-3 mr-1" />
                Coupons Available
              </Badge>
            </div>
          )}

          {location.ads && location.ads.filter(ad => isCurrentlyActive(ad.AdStartDate, ad.AdEndDate)).length > 0 && (
            <div>
              <p className="text-sm font-medium mb-3">Store Ads</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {location.ads
                  .filter(ad => isCurrentlyActive(ad.AdStartDate, ad.AdEndDate))
                  .map((ad, idx) => (
                  <Button
                    key={idx}
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setSelectedAd(ad);
                      setAdViewerOpen(true);
                    }}
                    disabled={!ad.AdFile?.url}
                  >
                    <IconFileText className="h-4 w-4 mr-2" />
                    {ad.ad_type?.AdType || 'View Ad'}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {location.LocationServices && location.LocationServices.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-3">Services</p>
              <div className="flex flex-wrap gap-2">
                {location.LocationServices.map((service, idx) => (
                  <Badge
                    key={idx}
                    variant="default"
                    className={`bg-primary text-primary-foreground ${service.LocationServiceShowMore ? 'cursor-pointer hover:bg-primary/90' : ''}`}
                    onClick={() => {
                      if (service.LocationServiceShowMore) {
                        setSelectedService(service);
                        setServiceSheetOpen(true);
                      }
                    }}
                  >
                    {service.LocationServiceShowMore && <IconInfoCircle className="h-3 w-3 mr-1" />}
                    {service.LocationServiceDepartment}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {location.LocationLinks && location.LocationLinks.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-3">Links</p>
              <div className="flex flex-wrap gap-2">
                {location.LocationLinks.map((link, idx) => {
                  const linkInfo = getLinkInfo(link.LocationLink);
                  return (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      asChild
                    >
                      <a
                        href={link.LocationLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <linkInfo.icon className="h-4 w-4 mr-2" />
                        {linkInfo.label}
                      </a>
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

              </DialogContent>
      </Dialog>

      <AdViewerSheet
        open={adViewerOpen}
        onOpenChange={setAdViewerOpen}
        ad={selectedAd}
        locationTitle={location.LocationTitle}
        locationBanner={location.LocationBanner}
      />

      <Sheet open={serviceSheetOpen} onOpenChange={setServiceSheetOpen}>
        <SheetContent side="right" className="z-[100] w-full sm:w-[90vw] sm:max-w-2xl p-0 flex flex-col">
          <SheetHeader className="px-6 pt-6 pb-4 border-b text-left">
            <SheetTitle className="text-2xl">{selectedService?.LocationServiceDepartment}</SheetTitle>
          </SheetHeader>

          {(() => {
            const sections = parseContentSections(selectedService?.LocationServiceDetails);
            const hasMultipleSections = sections.length > 1;

            return (
              <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                {/* Service Image */}
                {selectedService?.LocationServiceImage?.url && (
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={selectedService.LocationServiceImage.url}
                      alt={selectedService.LocationServiceImage.name || selectedService.LocationServiceDepartment}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                )}

                {/* Service Details - Tabbed if multiple sections, simple if single */}
                {selectedService?.LocationServiceDetails && (
                  hasMultipleSections ? (
                    <Tabs defaultValue={sections[0]?.title} className="w-full">
                      <TabsList className="w-full flex-wrap h-auto gap-1 bg-muted/50 p-1">
                        {sections.map((section, idx) => (
                          <TabsTrigger
                            key={idx}
                            value={section.title}
                            className="flex-1 min-w-fit text-xs sm:text-sm"
                          >
                            {section.title}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      {sections.map((section, idx) => (
                        <TabsContent key={idx} value={section.title} className="mt-4">
                          <div className="prose prose-sm max-w-none">
                            <RichTextRenderer content={section.content} />
                          </div>
                        </TabsContent>
                      ))}
                    </Tabs>
                  ) : (
                    <div className="prose prose-sm max-w-none">
                      <RichTextRenderer content={selectedService.LocationServiceDetails} />
                    </div>
                  )
                )}
              </div>
            );
          })()}
        </SheetContent>
      </Sheet>
    </>
  );
};

interface StoreLocatorProps {
  className?: string;
}

export const StoreLocator = ({ className = '' }: StoreLocatorProps) => {
  const { data: locations, isLoading } = useLocations();
  const { selectedLocation: persistedLocation, setLocation, isSelected, clearLocation } = useUserLocation();
  const { position: userPosition, getCurrentPosition, isLoading: isGeolocating } = useGeolocation();
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [geocodedCoordinates, setGeocodedCoordinates] = useState<Map<string, [number, number]>>(new Map());

  // Geocode all locations on load
  useEffect(() => {
    if (!locations || locations.length === 0) return;

    const geocodeLocations = async () => {
      const addressMap = new Map<string, string>();
      for (const location of locations) {
        const addressString = [
          location.LocationAddress.LocationAddressLineOne,
          location.LocationAddress.LocationAddressCity,
          location.LocationAddress.LocationAddressState,
          location.LocationAddress.LocationAddressZipCode,
        ].filter(Boolean).join(', ');

        if (addressString) {
          addressMap.set(location.LocationTitle, addressString);
        }
      }

      const geocodedMap = await batchGeocodeAddresses(Array.from(addressMap.values()));

      // Map back to location titles
      const coordsByTitle = new Map<string, [number, number]>();
      for (const [title, address] of addressMap) {
        const coords = geocodedMap.get(address);
        if (coords) {
          coordsByTitle.set(title, coords);
        }
      }
      setGeocodedCoordinates(coordsByTitle);
    };

    geocodeLocations();
  }, [locations]);

  // Locations with distance calculations
  const locationsWithDistance = useMemo<LocationWithDistance[]>(() => {
    if (!locations) return [];

    return locations.map((location) => {
      const coordinates = geocodedCoordinates.get(location.LocationTitle);
      const distance = userPosition && coordinates
        ? calculateDistance(userPosition.latitude, userPosition.longitude, coordinates[0], coordinates[1])
        : undefined;
      return { ...location, distance, coordinates };
    });
  }, [locations, userPosition, geocodedCoordinates]);

  // Filter locations based on search query (searches ALL locations)
  const filteredLocations = useMemo(() => {
    if (!searchQuery.trim()) return locationsWithDistance;
    const query = searchQuery.toLowerCase();
    return locationsWithDistance.filter((location) => {
      const { LocationAddress, LocationTitle, LocationBanner } = location;
      return (
        LocationTitle?.toLowerCase().includes(query) ||
        LocationBanner?.toLowerCase().includes(query) ||
        LocationAddress?.LocationAddressCity?.toLowerCase().includes(query) ||
        LocationAddress?.LocationAddressState?.toLowerCase().includes(query) ||
        LocationAddress?.LocationAddressZipCode?.toLowerCase().includes(query) ||
        LocationAddress?.LocationAddressLineOne?.toLowerCase().includes(query)
      );
    });
  }, [locationsWithDistance, searchQuery]);

  // Sort and prioritize locations: selected first, then alphabetically, then by distance
  const sortedLocations = useMemo(() => {
    const sorted = [...filteredLocations].sort((a, b) => {
      // Selected store always first
      const aSelected = isSelected(a.LocationTitle);
      const bSelected = isSelected(b.LocationTitle);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // Then sort alphabetically by title
      const titleCompare = a.LocationTitle.localeCompare(b.LocationTitle);
      if (titleCompare !== 0) return titleCompare;

      // Then sort by distance if available (as tiebreaker)
      const distA = a.distance ?? Infinity;
      const distB = b.distance ?? Infinity;
      return distA - distB;
    });
    return sorted;
  }, [filteredLocations, isSelected]);

  // Limit display: 3 on desktop, 1 on mobile (when not searching)
  const displayLocations = useMemo(() => {
    // When searching, show all results
    if (searchQuery.trim()) return sortedLocations;
    // Otherwise limit to 3
    return sortedLocations.slice(0, 3);
  }, [sortedLocations, searchQuery]);

  const handleViewDetails = (location: Location) => {
    setSelectedLocation(location);
    setDialogOpen(true);
  };

  const handleSelectLocation = (locationTitle: string) => {
    setLocation(locationTitle);
  };

  const handleUseCurrentLocation = () => {
    getCurrentPosition();
  };

  // Hide component for single-location stores
  if (!isLoading && locations && locations.length <= 1) {
    return null;
  }

  const totalLocations = locations?.length ?? 0;
  // Show CTA only when there are more locations than displayed (>3)
  const showViewAllCta = totalLocations > 3 && !searchQuery.trim();

  return (
    <section className={`py-12 bg-background ${className}`.trim()}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-3xl font-bold mb-2 text-foreground">Find a Store Near You</h2>
            <p className="text-muted-foreground">Visit one of our convenient locations</p>
          </div>

          {/* Search and Location */}
          <div className="flex gap-2 w-full sm:w-auto">
            <Input
              type="text"
              placeholder="Enter ZIP code or city"
              className="flex-1 sm:w-64 border-border bg-stone-50"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <Button variant="outline" onClick={() => setSearchQuery('')}>
                Clear
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={handleUseCurrentLocation}
              disabled={isGeolocating}
              title="Use my current location"
              className="flex-shrink-0"
            >
              <IconCurrentLocation className={`h-4 w-4 ${isGeolocating ? 'animate-pulse' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className={`hover:shadow-lg transition-shadow ${i > 1 ? 'hidden md:block' : ''}`}>
                <CardHeader>
                  <Skeleton className="h-6 w-32 mb-2" />
                  <Skeleton className="h-8 w-48" />
                </CardHeader>
                <CardContent className="space-y-4">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-10 w-full mt-4" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Store cards */}
        {!isLoading && displayLocations && displayLocations.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayLocations.map((location, index) => {
              const storeStatus = getStoreStatus(location.LocationHours);
              const fullAddress = [
                location.LocationAddress.LocationAddressLineOne,
                location.LocationAddress.LocationAddressLineTwo,
                `${location.LocationAddress.LocationAddressCity}, ${location.LocationAddress.LocationAddressState} ${location.LocationAddress.LocationAddressZipCode}`,
              ]
                .filter(Boolean)
                .join(', ');

              const isLocationSelected = isSelected(location.LocationTitle);
              // On mobile (no search), only show first card
              const mobileHiddenClass = !searchQuery.trim() && index > 0 ? 'hidden md:block' : '';

              return (
                <Card
                  key={location.LocationTitle}
                  className={`relative hover:shadow-lg transition-shadow ${isLocationSelected ? 'ring-2 ring-primary' : ''} ${mobileHiddenClass}`}
                >
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge
                        className={`w-fit ${storeStatus.isOpen ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-rose-600 hover:bg-rose-700 text-white'}`}
                      >
                        <IconClock className="h-3 w-3 mr-1" />
                        {storeStatus.message}
                      </Badge>
                      {location.distance !== undefined && (
                        <Badge variant="secondary" className="w-fit">
                          <IconMapPin className="h-3 w-3 mr-1" />
                          {location.distance.toFixed(1)} mi
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-2xl">{location.LocationTitle}</CardTitle>
                      {isLocationSelected && (
                        <Badge variant="default" className="w-fit">
                          <IconCheck className="h-3 w-3 mr-1" />
                          Selected
                        </Badge>
                      )}
                    </div>
                    {location.LocationBanner && (
                      <p className="text-sm text-muted-foreground -mt-1">{location.LocationBanner}</p>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <IconMapPin className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      <span>{fullAddress}</span>
                    </div>
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <IconPhone className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      <span>{location.LocationPhoneNumber}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant={isLocationSelected ? "destructive" : "default"}
                        className="flex-1"
                        onClick={() => isLocationSelected ? clearLocation() : handleSelectLocation(location.LocationTitle)}
                      >
                        {isLocationSelected ? (
                          <>Clear Selection</>
                        ) : (
                          <>Set as My Store</>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleViewDetails(location)}
                      >
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* View All CTA */}
        {showViewAllCta && (
          <div className="mt-8 text-center">
            <Button variant="outline" size="lg" asChild>
              <Link to="/locations">
                View All {totalLocations} Locations
                <IconArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        )}

        {!isLoading && filteredLocations && filteredLocations.length === 0 && searchQuery && (
          <div className="text-center py-8 text-muted-foreground">
            No locations found matching "{searchQuery}". Try a different search.
          </div>
        )}

        {!isLoading && (!locations || locations.length === 0) && (
          <div className="text-center py-8 text-muted-foreground">
            No locations available at this time.
          </div>
        )}

        <LocationDetailsDialog
          location={selectedLocation}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      </div>
    </section>
  );
};





